import React, { useMemo, forwardRef } from 'react'
import { getTokenValue, Spinner as TSpinner, useTheme } from 'tamagui'
import type { Token } from 'tamagui'
import { useLingui } from '@lingui/react/macro'

type TamaguiSpinnerSize = React.ComponentProps<typeof TSpinner>['size']

const getTokenPrimitiveValue = (
  token: Token,
  group: 'size' | 'color'
): string | number | undefined => {
  const value: unknown = getTokenValue(token, group)

  return typeof value === 'string' || typeof value === 'number'
    ? value
    : undefined
}

const getThemeColor = (
  theme: ReturnType<typeof useTheme>,
  color: Token
): string | undefined => {
  if (typeof color !== 'string' || !color.startsWith('$')) return undefined

  const themeRecord = theme as unknown as Record<string, { val?: unknown }>
  const themeValue = themeRecord[color.slice(1)]?.val

  return typeof themeValue === 'string' ? themeValue : undefined
}

export const Spinner = forwardRef<
  typeof TSpinner,
  { color: Token; size: Token }
>(({ color, size, ...props }, ref) => {
  const { t } = useLingui()
  const theme = useTheme()

  const parsedColor = useMemo(() => {
    const themeColor = getThemeColor(theme, color)
    if (themeColor) return themeColor

    const tokenColor = getTokenPrimitiveValue(color, 'color')
    return tokenColor ? String(tokenColor) : undefined
  }, [color, theme])

  const parsedSize = useMemo(() => {
    return getTokenPrimitiveValue(size, 'size')
  }, [size])

  return (
    <TSpinner
      ref={ref}
      {...props}
      size={parsedSize as TamaguiSpinnerSize}
      color={parsedColor}
      aria-label={t`Loading...`}
    />
  )
})
Spinner.displayName = 'Spinner'
